import { useId, type ReactNode } from 'react';
import type { RoomDocument } from '../api/types';
import { formatDateTime } from '../lib/date-only';
import {
  PROCESSING_FILTERS,
  PROCESSING_FILTER_HINT,
  PROCESSING_FILTER_LABEL,
  UPLOAD_RANGES,
  UPLOAD_RANGE_LABEL,
  type ProcessingParams,
  type ProcessingViewResponse,
  type UploadRange,
} from '../lib/processing';
import { categoryPathLabel } from '../lib/period-tree';
import { cn } from '../lib/utils';
import { Button } from './button';
import { FileIndicatorsView, StatusChip, documentStatusKey, fileIndicatorsOf } from './status-chip';

export interface ProcessingStatusViewProps {
  params: ProcessingParams;
  onChange: (next: ProcessingParams) => void;
  data: ProcessingViewResponse | undefined;
  loading: boolean;
  error: string | null;
  /** The room's timezone (the upload days are read in it). */
  timeZone: string;
  /** Opens a document (its viewer, or its filing review). The file name opens it too. */
  onOpen?: (doc: RoomDocument) => void;
  /** Whether a document can be opened now (default: all can). A file still being
   * prepared has nothing to show yet: no button, and its name is plain text. */
  canOpen?: (doc: RoomDocument) => boolean;
  /** The words of the open action for a document, e.g. "Review filing" / "View". */
  openLabel?: (doc: RoomDocument) => string;
  /** Opens the redaction review of a document whose redaction needs a person. */
  onReviewRedaction?: (doc: RoomDocument) => void;
  /** A "?" help tip shown after the last status button. */
  statusAside?: ReactNode;
  /** A "?" help tip shown once, after the first redaction indicator in the list. */
  redactionAside?: ReactNode;
}

/**
 * The room's PROCESSING STATUS page body (owner ask 2026-10-12), shared by the
 * admin and seller apps: status selections with counts, an upload-date range
 * (presets or "Between [date] and [date]", inclusive, the room's timezone), and
 * the page of documents — name, category path, filing chip, OCR / redaction
 * indicators, uploader, upload time, and an open action. Stateless: the app owns
 * the URL and the query.
 */
export function ProcessingStatusView({
  params,
  onChange,
  data,
  loading,
  error,
  timeZone,
  onOpen,
  canOpen,
  openLabel,
  onReviewRedaction,
  statusAside,
  redactionAside,
}: ProcessingStatusViewProps) {
  const rangeId = useId();
  const fromId = useId();
  const toId = useId();
  const set = (patch: Partial<ProcessingParams>) => onChange({ ...params, page: 1, ...patch });
  // The redaction "?" shows once, on the first row that has a redaction indicator.
  const redactionAsideId = redactionAside
    ? data?.documents.find((d) => fileIndicatorsOf(d).redaction)?.id
    : undefined;

  return (
    <div className="space-y-4">
      <div role="group" aria-label="Status" className="flex flex-wrap gap-2">
        {PROCESSING_FILTERS.map((f) => {
          const active = params.status === f;
          const count = data?.counts[f];
          return (
            <button
              key={f}
              type="button"
              aria-pressed={active}
              title={PROCESSING_FILTER_HINT[f]}
              onClick={() => set({ status: f })}
              className={cn(
                'inline-flex h-8 items-center gap-1.5 rounded-pill border px-3 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                active
                  ? 'border-primary bg-primary text-primary-foreground'
                  : 'border-input bg-background hover:bg-surface-2',
              )}
            >
              {PROCESSING_FILTER_LABEL[f]}
              <span className={cn('tabular-nums', active ? 'opacity-90' : 'text-ink-3')}>
                {count ?? '…'}
              </span>
            </button>
          );
        })}
        {statusAside ? <span className="inline-flex h-8 items-center">{statusAside}</span> : null}
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <div className="space-y-1">
          <label htmlFor={rangeId} className="block text-xs font-medium text-ink-2">
            Uploaded
          </label>
          <select
            id={rangeId}
            value={params.range}
            onChange={(e) => set({ range: e.target.value as UploadRange })}
            className="h-9 rounded-md border border-input bg-background px-2 text-sm"
          >
            {UPLOAD_RANGES.map((r) => (
              <option key={r} value={r}>
                {UPLOAD_RANGE_LABEL[r]}
              </option>
            ))}
          </select>
        </div>
        {params.range === 'custom' ? (
          <>
            <div className="space-y-1">
              <label htmlFor={fromId} className="block text-xs font-medium text-ink-2">
                From
              </label>
              <input
                id={fromId}
                type="date"
                value={params.from ?? ''}
                max={params.to}
                onChange={(e) => set({ from: e.target.value || undefined })}
                className="h-9 rounded-md border border-input bg-background px-2 text-sm"
              />
            </div>
            <div className="space-y-1">
              <label htmlFor={toId} className="block text-xs font-medium text-ink-2">
                To (inclusive)
              </label>
              <input
                id={toId}
                type="date"
                value={params.to ?? ''}
                min={params.from}
                onChange={(e) => set({ to: e.target.value || undefined })}
                className="h-9 rounded-md border border-input bg-background px-2 text-sm"
              />
            </div>
          </>
        ) : null}
        <div className="space-y-1">
          <span className="block text-xs font-medium text-ink-2">Order</span>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => set({ order: params.order === 'desc' ? 'asc' : 'desc' })}
          >
            {params.order === 'desc' ? 'Newest first' : 'Oldest first'}
          </Button>
        </div>
        <p className="pb-2 text-xs text-ink-3">
          Days are read in the room's timezone ({timeZone}).
        </p>
      </div>

      {error ? (
        <p className="text-sm text-destructive" role="alert">
          {error}
        </p>
      ) : !data && loading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : data && data.documents.length === 0 ? (
        <p className="text-sm text-muted-foreground">No documents match.</p>
      ) : data ? (
        <>
          <ul aria-label="Documents" className="divide-y rounded-lg border">
            {data.documents.map((d) => (
              <li
                key={d.id}
                className="grid grid-cols-1 gap-2 p-3 text-sm md:grid-cols-[minmax(0,1fr)_220px_200px_auto] md:items-start md:gap-4"
              >
                <div className="min-w-0">
                  {onOpen && (canOpen?.(d) ?? true) ? (
                    <button
                      type="button"
                      title={openLabel ? openLabel(d) : 'Open'}
                      onClick={() => onOpen(d)}
                      className="block max-w-full truncate text-left font-medium hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    >
                      {d.name}
                    </button>
                  ) : (
                    <span className="block truncate font-medium">{d.name}</span>
                  )}
                  {d.categoryPath.length > 0 ? (
                    <span className="block truncate text-xs text-ink-3">
                      {categoryPathLabel(d.categoryPath)}
                    </span>
                  ) : null}
                </div>
                <div className="flex flex-col items-start gap-1">
                  <StatusChip
                    status={documentStatusKey(
                      d.classificationStatus,
                      d.status,
                      d.paperworkKind,
                      Boolean(d.archive),
                      d.processingStatus,
                      d.categoryPath.length > 0,
                    )}
                  />
                  <FileIndicatorsView
                    indicators={fileIndicatorsOf(d)}
                    onReviewRedaction={onReviewRedaction ? () => onReviewRedaction(d) : undefined}
                    subject={d.name}
                    redactionAside={d.id === redactionAsideId ? redactionAside : undefined}
                  />
                </div>
                <div className="min-w-0 text-xs text-ink-2">
                  <span className="block truncate">{d.uploadedBy ?? '—'}</span>
                  <span className="block tabular-nums text-ink-3">
                    {formatDateTime(d.createdAt)}
                  </span>
                </div>
                <div className="md:text-right">
                  {onOpen && (canOpen?.(d) ?? true) ? (
                    <Button type="button" variant="ghost" size="sm" onClick={() => onOpen(d)}>
                      {openLabel ? openLabel(d) : 'Open'}
                      <span className="sr-only"> {d.name}</span>
                    </Button>
                  ) : null}
                </div>
              </li>
            ))}
          </ul>
          <nav
            aria-label="Pages"
            className="flex flex-wrap items-center justify-between gap-2 text-sm"
          >
            <span className="text-ink-2">
              {data.total} {data.total === 1 ? 'document' : 'documents'} · page {data.page} of{' '}
              {data.pageCount}
            </span>
            <span className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={data.page <= 1}
                onClick={() => onChange({ ...params, page: data.page - 1 })}
              >
                Previous
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={data.page >= data.pageCount}
                onClick={() => onChange({ ...params, page: data.page + 1 })}
              >
                Next
              </Button>
            </span>
          </nav>
        </>
      ) : null}
    </div>
  );
}
